import React, { useState, useEffect } from 'react';
import {
  Activity, Users, FileText, MessageSquare, AlertTriangle,
  TrendingUp, Settings, Database, CheckCircle, XCircle,
  Clock, Server, HardDrive, X, Trash2, RotateCcw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL !== undefined && process.env.REACT_APP_BACKEND_URL !== '' ? process.env.REACT_APP_BACKEND_URL : '';

const ITDashboard = ({ setCurrentPage }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTiket, setSelectedTiket] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/it-dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (err) {
      console.error('Gagal mengambil dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTiketClick = async (tiketId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tiket-it/${tiketId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const tiket = await response.json();
        setSelectedTiket(tiket);
        setShowDetailModal(true);
      }
    } catch (err) {
      console.error('Gagal mengambil detail tiket:', err);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTiket) return;

    setSendingReply(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('balasan', replyText);

      const response = await fetch(`${API_URL}/api/tiket-it/${selectedTiket.id}/balas`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        setReplyText('');
        // Refresh tiket data
        await handleTiketClick(selectedTiket.id);
        // Refresh dashboard
        await fetchDashboardData();
      } else {
        const error = await response.json();
        console.error('Gagal mengirim balasan:', error.detail);
        alert(error.detail || 'Gagal mengirim balasan');
      }
    } catch (err) {
      console.error('Gagal mengirim balasan:', err);
      alert('Terjadi kesalahan saat mengirim balasan');
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedTiket) return;

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('status', newStatus);

      const response = await fetch(`${API_URL}/api/tiket-it/${selectedTiket.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        // Refresh tiket data
        await handleTiketClick(selectedTiket.id);
        // Refresh dashboard
        await fetchDashboardData();
      } else {
        const error = await response.json();
        console.error('Gagal update status:', error.detail);
        alert(error.detail || 'Gagal update status tiket');
      }
    } catch (err) {
      console.error('Gagal update status:', err);
      alert('Terjadi kesalahan saat update status');
    }
  };

  const handleDeleteTiket = async () => {
    if (!selectedTiket) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tiket-it/${selectedTiket.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setShowDeleteConfirm(false);
        setShowDetailModal(false);
        setSelectedTiket(null);
        // Refresh dashboard
        await fetchDashboardData();
        alert('Tiket berhasil dihapus');
      } else {
        const error = await response.json();
        alert(error.detail || 'Gagal menghapus tiket');
      }
    } catch (err) {
      console.error('Gagal menghapus tiket:', err);
      alert('Terjadi kesalahan saat menghapus tiket');
    } finally {
      setDeleting(false);
    }
  };

  const handleResetAllTikets = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tiket-it/reset`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setShowResetConfirm(false);
        // Refresh dashboard
        await fetchDashboardData();
        alert('Semua data tiket berhasil direset');
      } else {
        const error = await response.json();
        alert(error.detail || 'Gagal mereset data tiket');
      }
    } catch (err) {
      console.error('Gagal mereset tiket:', err);
      alert('Terjadi kesalahan saat mereset data tiket');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-gray-600">Memuat dashboard IT...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-red-900 mb-2">Gagal Memuat Dashboard</h3>
        <p className="text-red-700">Terjadi kesalahan saat mengambil data dashboard</p>
      </div>
    );
  }

  const { tiket_stats, recent_tiket, system_stats } = dashboardData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Settings size={36} className="text-primary-600" />
            Dashboard IT
          </h1>
          <p className="text-gray-600 mt-1">
            Monitoring sistem, tiket support, dan manajemen platform
          </p>
        </div>
        <button
          onClick={() => setShowResetConfirm(true)}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <RotateCcw size={16} />
          Reset Data Tiket
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Tiket */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-semibold">Total Tiket</p>
              <p className="text-3xl font-bold mt-1">{tiket_stats.total}</p>
            </div>
            <MessageSquare size={48} className="text-blue-200 opacity-50" />
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm text-blue-100">
            <AlertTriangle size={16} />
            <span>{tiket_stats.open} tiket open</span>
          </div>
        </div>

        {/* Tiket Selesai */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-semibold">Tiket Selesai</p>
              <p className="text-3xl font-bold mt-1">{tiket_stats.resolved}</p>
            </div>
            <CheckCircle size={48} className="text-green-200 opacity-50" />
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm text-green-100">
            <Clock size={16} />
            <span>{tiket_stats.in_progress} dalam proses</span>
          </div>
        </div>

        {/* Total Users */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-semibold">Total Users</p>
              <p className="text-3xl font-bold mt-1">{system_stats.total_users}</p>
            </div>
            <Users size={48} className="text-purple-200 opacity-50" />
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm text-purple-100">
            <CheckCircle size={16} />
            <span>{system_stats.active_users} aktif</span>
          </div>
        </div>

        {/* System Logs */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-semibold">System Logs</p>
              <p className="text-3xl font-bold mt-1">{system_stats.total_logs}</p>
            </div>
            <Server size={48} className="text-orange-200 opacity-50" />
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm text-orange-100">
            <XCircle size={16} />
            <span>{system_stats.recent_errors} error (24h)</span>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tiket */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <MessageSquare size={24} />
              Tiket Terbaru
            </h2>
          </div>
          <div className="p-6">
            {recent_tiket.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Belum ada tiket</p>
            ) : (
              <div className="space-y-3">
                {recent_tiket.map((tiket) => (
                  <div
                    key={tiket.id}
                    onClick={() => handleTiketClick(tiket.id)}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            tiket.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                            tiket.status === 'resolved' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {tiket.status}
                          </span>
                          <span className="text-xs text-gray-500">{tiket.kategori}</span>
                        </div>
                        <h4 className="font-semibold text-gray-900">{tiket.subjek}</h4>
                        <p className="text-sm text-gray-600 line-clamp-1">{tiket.pesan}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{tiket.pengirim_role}</span>
                      <span>{new Date(tiket.created_at).toLocaleDateString('id-ID')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Activity size={24} />
              Aksi Cepat
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <button onClick={() => setCurrentPage && setCurrentPage('verifikasi')} className="p-4 border-2 border-primary-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all text-left">
                <FileText size={32} className="text-primary-600 mb-2" />
                <h3 className="font-semibold text-gray-900">Lihat Semua Laporan</h3>
                <p className="text-sm text-gray-600">Akses laporan shift</p>
              </button>

              <button onClick={() => setCurrentPage && setCurrentPage('users')} className="p-4 border-2 border-blue-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left">
                <Users size={32} className="text-blue-600 mb-2" />
                <h3 className="font-semibold text-gray-900">Manajemen User</h3>
                <p className="text-sm text-gray-600">Kelola user & role</p>
              </button>

              <button onClick={() => setCurrentPage && setCurrentPage('system-logs')} className="p-4 border-2 border-green-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left">
                <Database size={32} className="text-green-600 mb-2" />
                <h3 className="font-semibold text-gray-900">System Logs</h3>
                <p className="text-sm text-gray-600">Monitoring aktivitas</p>
              </button>

              <button onClick={() => setCurrentPage && setCurrentPage('tanya-it')} className="p-4 border-2 border-purple-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left">
                <MessageSquare size={32} className="text-purple-600 mb-2" />
                <h3 className="font-semibold text-gray-900">Kelola Tiket</h3>
                <p className="text-sm text-gray-600">Lihat & balas tiket</p>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* System Stats Grid */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Server size={24} className="text-primary-600" />
          Statistik Sistem
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <HardDrive size={32} className="mx-auto text-gray-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{system_stats.total_laporan}</p>
            <p className="text-sm text-gray-600">Total Laporan</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <Activity size={32} className="mx-auto text-blue-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{system_stats.total_logs}</p>
            <p className="text-sm text-gray-600">Total Logs</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <Users size={32} className="mx-auto text-green-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{system_stats.active_users}</p>
            <p className="text-sm text-gray-600">User Aktif</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <AlertTriangle size={32} className="mx-auto text-red-600 mb-2" />
            <p className="text-2xl font-bold text-gray-900">{system_stats.recent_errors}</p>
            <p className="text-sm text-gray-600">Error (24h)</p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-primary-50 to-blue-50 border-2 border-primary-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <CheckCircle size={32} className="text-primary-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Dashboard IT Aktif</h3>
            <p className="text-gray-700 mb-2">
              Anda memiliki akses penuh ke semua fitur sistem sebagai role IT. Termasuk:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>Edit dan hapus semua transaksi</li>
              <li>Monitoring sistem logs dan error</li>
              <li>Manajemen tiket support</li>
              <li>Akses dashboard lengkap (Owner features)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Tiket Detail Modal */}
      {showDetailModal && selectedTiket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 sticky top-0">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">Detail Tiket</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-1.5 text-sm"
                    disabled={deleting}
                  >
                    <Trash2 size={16} />
                    Hapus Tiket
                  </button>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  selectedTiket.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                  selectedTiket.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                  selectedTiket.status === 'resolved' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  Status: {selectedTiket.status}
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
                  {selectedTiket.kategori}
                </span>
                <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
                  {selectedTiket.prioritas}
                </span>
              </div>

              {/* Tiket Info */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="mb-3">
                  <label className="text-sm font-semibold text-gray-600">Subjek:</label>
                  <p className="text-lg font-semibold text-gray-900">{selectedTiket.subjek}</p>
                </div>
                <div className="mb-3">
                  <label className="text-sm font-semibold text-gray-600">Pesan:</label>
                  <p className="text-gray-700 bg-white p-3 rounded border">{selectedTiket.pesan}</p>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>Pengirim: {selectedTiket.pengirim_role}</span>
                  <span>•</span>
                  <span>{new Date(selectedTiket.created_at).toLocaleString('id-ID')}</span>
                </div>
              </div>

              {/* Balasan */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Riwayat Balasan</h3>
                {selectedTiket.balasan && selectedTiket.balasan.length > 0 ? (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {selectedTiket.balasan.map((balasan, index) => (
                      <div key={index} className={`p-3 rounded-lg ${
                        balasan.pengirim === 'IT'
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : 'bg-gray-50 border-l-4 border-gray-400'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-900">
                            {balasan.pengirim_nama || balasan.pengirim}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(balasan.timestamp).toLocaleString('id-ID')}
                          </span>
                        </div>
                        <p className="text-gray-700">{balasan.pesan}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Belum ada balasan</p>
                )}
              </div>

              {/* Update Status (IT Only) */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Update Status</h3>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => handleUpdateStatus('open')}
                    disabled={selectedTiket.status === 'open'}
                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Open
                  </button>
                  <button
                    onClick={() => handleUpdateStatus('in_progress')}
                    disabled={selectedTiket.status === 'in_progress'}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    In Progress
                  </button>
                  <button
                    onClick={() => handleUpdateStatus('resolved')}
                    disabled={selectedTiket.status === 'resolved'}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Resolved
                  </button>
                  <button
                    onClick={() => handleUpdateStatus('closed')}
                    disabled={selectedTiket.status === 'closed'}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Closed
                  </button>
                </div>
              </div>

              {/* Balas Form */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Balas Tiket</h3>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Tulis balasan..."
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows="3"
                />
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={handleSendReply}
                    disabled={!replyText.trim() || sendingReply}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {sendingReply ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Mengirim...
                      </>
                    ) : (
                      <>
                        <MessageSquare size={16} />
                        Kirim Balasan
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Konfirmasi Hapus Tiket Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <Trash2 size={24} className="text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Hapus Tiket?</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Apakah Anda yakin ingin menghapus tiket ini? Tindakan ini tidak dapat dibatalkan.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={deleting}
                >
                  Batal
                </button>
                <button
                  onClick={handleDeleteTiket}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={deleting}
                >
                  {deleting ? 'Menghapus...' : 'Ya, Hapus'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Konfirmasi Reset Data Tiket Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-orange-100 rounded-full">
                  <RotateCcw size={24} className="text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Reset Semua Tiket?</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Apakah Anda yakin ingin mereset semua data tiket? Tindakan ini akan:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1 text-sm">
                <li>Menghapus semua tiket yang ada</li>
                <li>Menghapus semua balasan tiket</li>
                <li>Tindakan ini tidak dapat dibatalkan</li>
              </ul>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={deleting}
                >
                  Batal
                </button>
                <button
                  onClick={handleResetAllTikets}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={deleting}
                >
                  {deleting ? 'Mereset...' : 'Ya, Reset Semua'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ITDashboard;
